import type { AnyFieldApi } from '@tanstack/react-form'
import type { CreateOperatorData } from '@/api/resources/machine-operators'
import { useForm } from '@tanstack/react-form'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import {
  attachableUsersQueryOptions,
  attachOperator,
  createOperator,
} from '@/api/resources/machine-operators'
import { FormInput } from '@/components/forms/form-input'
import { FormSwitch } from '@/components/forms/form-switch'
import { AddOperatorDialog } from '@/components/machines/operators/add-operator-dialog'
import {
  AttachableUserRow,
  SearchSkeletonRow,
} from '@/components/machines/operators/attachable-user-row'
import { Button } from '@/components/ui/button'
import { DialogClose, DialogFooter } from '@/components/ui/dialog'
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from '@/components/ui/field'
import { SearchInput } from '@/components/ui/search-input'
import { Switch } from '@/components/ui/switch'
import { useRouteModal } from '@/hooks/use-route-modal'
import { useServerErrors } from '@/hooks/use-server-errors'
import { queryKeys } from '@/lib/query-keys'

const createOperatorSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.email('Please enter a valid email address'),
  email_optin: z.boolean().optional(),
})

export const Route = createFileRoute('/_app/machines/$machineCode/operators_/add')({
  head: () => ({ meta: [{ title: 'Add Operator - MyJunea' }] }),
  component: AddOperatorRoute,
})

function AddOperatorRoute(): React.ReactElement {
  const { machineCode } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const { open, handleClose, handleOpenChange } = useRouteModal(
    () =>
      void navigate({ to: '/machines/$machineCode', params: { machineCode }, resetScroll: false }),
  )

  const createMutation = useMutation({
    mutationFn: async (data: CreateOperatorData) => createOperator(machineCode, data),
    onSuccess: () => {
      clearServerErrors()
      void queryClient.invalidateQueries({ queryKey: queryKeys.machines.operators(machineCode) })
      toast.success('Operator created successfully')
      handleClose()
    },
    onError: handleMutationError,
  })

  const form = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      email_optin: false,
    } as CreateOperatorData,
    validators: { onSubmit: createOperatorSchema },
    onSubmit: ({ value }) => createMutation.mutate(value),
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, debouncer] = useDebouncedValue(searchQuery, { wait: 300 }, (state) => ({
    isPending: state.isPending,
  }))
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [attachEmailOptin, setAttachEmailOptin] = useState(false)

  useEffect(() => {
    setSelectedUserId(null)
    setAttachEmailOptin(false)
  }, [searchQuery])

  const { data: attachableUsersData, isLoading: isFetching } = useQuery(
    attachableUsersQueryOptions(machineCode, debouncedSearch),
  )
  const attachableUsers = attachableUsersData?.data ?? []
  const isSearching = debouncer.state.isPending || isFetching
  const hasValidSelection = attachableUsers.some((u) => u.id === selectedUserId)

  const attachMutation = useMutation({
    mutationFn: async () =>
      attachOperator(machineCode, { user_id: selectedUserId!, email_optin: attachEmailOptin }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.machines.operators(machineCode) })
      toast.success('Operator attached successfully')
      handleClose()
    },
    onError: handleMutationError,
  })

  const handleCreateSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    clearServerErrors()
    createMutation.reset()
    void form.handleSubmit()
  }

  return (
    <AddOperatorDialog
      open={open}
      onOpenChange={handleOpenChange}
      createTabContent={
        <form onSubmit={handleCreateSubmit} className="space-y-4">
          <form.Field name="first_name">
            {(field: AnyFieldApi) => (
              <FormInput
                field={field}
                label="First name"
                placeholder="Laura"
                serverErrors={serverErrors}
              />
            )}
          </form.Field>

          <form.Field name="last_name">
            {(field: AnyFieldApi) => (
              <FormInput
                field={field}
                label="Last name"
                placeholder="van den Berg"
                serverErrors={serverErrors}
              />
            )}
          </form.Field>

          <form.Field name="email">
            {(field: AnyFieldApi) => (
              <FormInput
                field={field}
                label="Email"
                type="email"
                placeholder="laura@example.com"
                serverErrors={serverErrors}
              />
            )}
          </form.Field>

          <form.Field name="email_optin">
            {(field: AnyFieldApi) => (
              <FormSwitch
                field={field}
                label="Email notifications"
                description="Notify user when the machine can be refilled."
                serverErrors={serverErrors}
              />
            )}
          </form.Field>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      }
      attachTabContent={
        <>
          <SearchInput
            placeholder="Search by name or email..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />

          <div className="space-y-2">
            {searchQuery.length < 2 ? (
              <p className="text-muted-foreground py-4 text-center text-sm">
                Type at least 2 characters to search.
              </p>
            ) : isSearching ? (
              <SearchSkeletonRow />
            ) : attachableUsers.length === 0 ? (
              <p className="text-muted-foreground py-4 text-center text-sm">No users found.</p>
            ) : (
              attachableUsers.map((user) => (
                <AttachableUserRow
                  key={user.id}
                  user={user}
                  isSelected={selectedUserId === user.id}
                  onSelect={() => setSelectedUserId(user.id)}
                />
              ))
            )}
          </div>

          {hasValidSelection && (
            <FieldLabel htmlFor="attach-email-optin">
              <Field orientation="horizontal">
                <FieldContent>
                  <FieldTitle>Email notifications</FieldTitle>
                  <FieldDescription>Notify user when the machine can be refilled.</FieldDescription>
                </FieldContent>
                <Switch
                  id="attach-email-optin"
                  checked={attachEmailOptin}
                  onCheckedChange={setAttachEmailOptin}
                />
              </Field>
            </FieldLabel>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              disabled={!hasValidSelection || attachMutation.isPending}
              onClick={() => attachMutation.mutate()}
            >
              {attachMutation.isPending ? 'Attaching...' : 'Attach'}
            </Button>
          </DialogFooter>
        </>
      }
    />
  )
}
